import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, AlertCircle, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { usePayloadSchema } from "@/hooks/usePayloads";
import { SchemaFormRenderer, countSchemaFields, type FieldFilter } from "./SchemaFormRenderer";
import type { CategoryId } from "@/utils/payloadCategorization";
import type { ProfilePayloadSummary } from "@/types/api";
import { cn } from "@/lib/utils";
import { HomeScreenLayoutEditor } from "./homescreen/HomeScreenLayoutEditor";

const HOME_SCREEN_IDENTIFIER = "com.apple.homescreenlayout";

function isHomeScreenPayload(payload: ProfilePayloadSummary): boolean {
  const id = (payload.identifier || "").toLowerCase().trim();
  const title = (payload.title || "").toLowerCase().trim();
  if (!id && !title) return false;
  // Tolerate camelCase / spacing variants from the API.
  if (id.replace(/\s+/g, "") === HOME_SCREEN_IDENTIFIER) return true;
  if (id.endsWith(".homescreenlayout")) return true;
  if (title === "home screen layout") return true;
  return false;
}

interface PayloadConfigPanelProps {
  payload: ProfilePayloadSummary;
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

const SHOW_TOOLBAR_THRESHOLD = 15;
const STORAGE_PREFIX = "payloadPanel:viewState:";

interface PersistedViewState {
  search: string;
  filter: FieldFilter;
  showAdvanced: boolean;
  openCategories: Partial<Record<CategoryId, boolean>>;
}

const DEFAULT_STATE: PersistedViewState = {
  search: "",
  filter: "all",
  showAdvanced: false,
  openCategories: {},
};

function isValidCategoryId(v: string): v is CategoryId {
  return ["sharing", "accounts", "apps", "media", "security", "network", "system", "other"].includes(v);
}

function readPersistedState(key: string): PersistedViewState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedViewState>;
    const filter: FieldFilter =
      parsed.filter === "modified" || parsed.filter === "required" ? parsed.filter : "all";
    const rawCats = parsed.openCategories;
    const openCategories: Partial<Record<CategoryId, boolean>> = {};
    if (rawCats && typeof rawCats === "object") {
      for (const [k, v] of Object.entries(rawCats)) {
        if (isValidCategoryId(k) && typeof v === "boolean") openCategories[k] = v;
      }
    }
    return {
      search: typeof parsed.search === "string" ? parsed.search : "",
      filter,
      showAdvanced: typeof parsed.showAdvanced === "boolean" ? parsed.showAdvanced : false,
      openCategories,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function writePersistedState(key: string, state: PersistedViewState) {
  if (typeof window === "undefined") return;
  try {
    const noCategories = Object.keys(state.openCategories).length === 0;
    // If everything is at defaults, clear the entry to keep storage clean.
    if (!state.search && state.filter === "all" && state.showAdvanced === false && noCategories) {
      window.localStorage.removeItem(STORAGE_PREFIX + key);
      return;
    }
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(state));
  } catch {
    /* ignore quota/serialization errors */
  }
}

export function PayloadConfigPanel({ payload, config, onConfigChange }: PayloadConfigPanelProps) {
  const isHomeScreen = isHomeScreenPayload(payload);

  const { data: schemaResponse, isLoading, error, refetch } = usePayloadSchema(
    payload.kind || "",
    payload.identifier || "",
    !isHomeScreen
  );

  // Stable per-payload key (kind + identifier). Falls back to whichever is defined.
  const storageKey = `${payload.kind || "_"}::${payload.identifier || "_"}`;

  // Initialize state from localStorage (lazy initializer runs once per mount).
  const [search, setSearch] = useState<string>(() => readPersistedState(storageKey).search);
  const [filter, setFilter] = useState<FieldFilter>(() => readPersistedState(storageKey).filter);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    () => readPersistedState(storageKey).showAdvanced
  );
  const [openCategories, setOpenCategories] = useState<Partial<Record<CategoryId, boolean>>>(
    () => readPersistedState(storageKey).openCategories
  );

  // If the panel is reused for a different payload, re-hydrate state for the new key.
  const lastKeyRef = useRef(storageKey);
  useEffect(() => {
    if (lastKeyRef.current === storageKey) return;
    lastKeyRef.current = storageKey;
    const persisted = readPersistedState(storageKey);
    setSearch(persisted.search);
    setFilter(persisted.filter);
    setShowAdvanced(persisted.showAdvanced);
    setOpenCategories(persisted.openCategories);
  }, [storageKey]);

  // Persist whenever any view-state field changes for the active key.
  useEffect(() => {
    writePersistedState(storageKey, { search, filter, showAdvanced, openCategories });
  }, [storageKey, search, filter, showAdvanced, openCategories]);

  const schema = schemaResponse?.jsonSchema as Record<string, any> | undefined;
  const values = config as Record<string, any>;

  const { total, modified, advancedHidden } = useMemo(
    () => countSchemaFields(schema, values),
    [schema, values]
  );

  const showToolbar = total > SHOW_TOOLBAR_THRESHOLD;

  // True when any view-state setting differs from defaults (excluding showAdvanced,
  // which is a separate visibility preference, not a "view filter" the user is
  // trying to undo with this control).
  const viewIsCustomized =
    !!search || filter !== "all" || Object.keys(openCategories).length > 0;

  const handleClearView = () => {
    // Snapshot the prior state so the user can undo via the toast.
    const prev = { search, filter, openCategories };
    setSearch("");
    setFilter("all");
    setOpenCategories({});
    toast("View cleared", {
      description: "Search, filter, and expanded categories were reset.",
      action: {
        label: "Undo",
        onClick: () => {
          setSearch(prev.search);
          setFilter(prev.filter);
          setOpenCategories(prev.openCategories);
        },
      },
    });
  };

  const handleResetAll = () => {
    if (!schema?.properties) return;
    const defaults: Record<string, any> = {};
    Object.entries(schema.properties as Record<string, any>).forEach(([k, p]) => {
      if (p.default !== undefined) defaults[k] = p.default;
    });
    onConfigChange(defaults);
  };

  if (isHomeScreen) {
    return (
      <HomeScreenLayoutEditor
        value={config}
        onChange={(next) => onConfigChange(next)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading schema for {payload.title || payload.identifier}…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-destructive">Failed to load schema</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          {error instanceof Error ? error.message : "An unexpected error occurred"}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  if (!schemaResponse?.jsonSchema) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No schema available for this payload. Configuration will use defaults.
      </p>
    );
  }

  const advancedSwitchId = `show-advanced-${storageKey}`;

  return (
    <div className="space-y-4">
      <div className="border-b pb-3">
        <h3 className="font-medium">{schemaResponse.title || payload.title || payload.identifier}</h3>
        {schemaResponse.description && (
          <p className="text-sm text-muted-foreground mt-1">{schemaResponse.description}</p>
        )}
      </div>

      {showToolbar && (
        <div className="sticky top-0 z-10 -mx-1 px-1 pt-1 pb-3 bg-background space-y-2 border-b">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search settings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-8 h-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1 items-center">
              {(["all", "modified", "required"] as FieldFilter[]).map((f) => (
                <Button
                  key={f}
                  type="button"
                  size="sm"
                  variant={filter === f ? "default" : "outline"}
                  className={cn("h-9 capitalize", filter === f && "shadow-sm")}
                  onClick={() => setFilter(f)}
                >
                  {f === "all" ? "All" : f === "modified" ? "Modified only" : "Required only"}
                </Button>
              ))}
              {advancedHidden > 0 || showAdvanced ? (
                <div className="flex items-center gap-2 ml-1 pl-2 border-l h-9">
                  <Switch
                    id={advancedSwitchId}
                    checked={showAdvanced}
                    onCheckedChange={setShowAdvanced}
                  />
                  <Label
                    htmlFor={advancedSwitchId}
                    className="text-xs font-medium cursor-pointer whitespace-nowrap"
                  >
                    Show advanced
                  </Label>
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              <span className="font-medium text-foreground">{modified}</span> of {total} settings modified
              {!showAdvanced && advancedHidden > 0 && (
                <> · <span>{advancedHidden} advanced hidden</span></>
              )}
            </span>
            <div className="flex items-center gap-3">
              {viewIsCustomized && (
                <button
                  type="button"
                  onClick={handleClearView}
                  className="hover:underline font-medium"
                >
                  Clear view
                </button>
              )}
              {modified > 0 && (
                <button
                  type="button"
                  onClick={handleResetAll}
                  className="text-primary hover:underline font-medium"
                >
                  Reset all
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SchemaFormRenderer
        schema={schema as Record<string, any>}
        values={values}
        onChange={onConfigChange}
        search={search}
        filter={filter}
        showAdvanced={showAdvanced}
        openCategories={openCategories}
        onOpenCategoriesChange={setOpenCategories}
      />
    </div>
  );
}
